import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { MediaItemUpload } from '../models/media';
import { MediaUploadStatus } from '../constants/media';
import {
  BulkAddExternalPhotosMutation,
  BulkCompleteMediaUploadsMutation,
  BulkRegisterMediaUploadsMutation,
  ExternalMediaInput,
  MediaType,
  RegisterMediaInput,
  UploadMediaInput,
  useBulkAddExternalPhotosMutation,
  useBulkCompleteMediaUploadsMutation,
  useBulkRegisterMediaUploadsMutation
} from '../models/graphql';
import { AppContext } from '../providers/AppProvider';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  getArrayBufferFromFile,
  getArrayBufferFromUrl,
  getImageDimensions,
  getMediaProportion,
  getUploadIdFromFileOrUrl,
  getVideoDimensions,
  isGifAnimated
} from '../helpers/media';
import { FileWithPreview } from 'modules/Publish/MediaLibrary/AddMediaSelectorModal/AddMediaSelectorModal.view';
import { FetchResult } from '@apollo/client/link/core/types';

gql`
  mutation BulkRegisterMediaUploads($workspaceId: ID!, $input: [RegisterMediaInput!]!) {
    bulkRegisterMediaItemUploads(workspaceId: $workspaceId, input: $input) {
      suppliedUploadId
      registrationKey
      uploadUrl
    }
  }

  mutation BulkCompleteMediaUploads($workspaceId: ID!, $input: [UploadMediaInput!]!) {
    bulkCompleteMediaUploads(workspaceId: $workspaceId, input: $input) {
      suppliedRegistrationKey
      error
      media {
        ...CoreMediaLibraryItemFields
        ... on Photo {
          ...CoreMediaLibraryPhotoFields
        }
        ... on Video {
          ...CoreMediaLibraryVideoFields
        }
      }
    }
  }

  mutation BulkAddExternalPhotos($workspaceId: ID!, $input: [ExternalMediaInput!]!) {
    bulkAddExternalPhotos(workspaceId: $workspaceId, input: $input) {
      suppliedRegistrationKey
      error
      media {
        ...CoreMediaLibraryItemFields
        ... on Photo {
          ...CoreMediaLibraryPhotoFields
        }
        ... on Video {
          ...CoreMediaLibraryVideoFields
        }
      }
    }
  }
`;

export type OnUploadStatusChangeCallbackType = (currentUpload: MediaItemUpload, status: MediaUploadStatus) => void;
export enum SourceType {
  external = 1,
  file
}
export type ExternalMedia = {
  type: MediaType;
  url: string;
  canvaId?: string;
  sourceType: SourceType.external;
  createdBy?: string;
  createdByUsername?: string;
};

export type MediaUploadType = {
  uploads: Record<string, MediaItemUpload>;
  queueUploads: (
    files: FileWithPreview[] | ExternalMedia[],
    onUploadStatusChange: OnUploadStatusChangeCallbackType
  ) => void;
  deleteUpload: (mediaItem: MediaItemUpload) => void;
};

export const useMediaUpload = (): MediaUploadType => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const [uploads, setUploads] = useState<Record<string, MediaItemUpload>>({});
  const prevState = useRef(uploads);

  const [bulkRegisterUploadsMutation] = useBulkRegisterMediaUploadsMutation();

  const [bulkCompleteUploadsMutation] = useBulkCompleteMediaUploadsMutation();

  const [bulkAddExternalPhotosMutation] = useBulkAddExternalPhotosMutation();

  const deleteUpload = useCallback((mediaItem: MediaItemUpload) => {
    mediaItem.status = MediaUploadStatus.DELETED;
    setUploads(old => ({ ...old, ...{ [mediaItem.id]: mediaItem } }));
  }, []);

  // Grabs the buffer of a file or url source type
  const getMediaUploadItemBuffer = (
    sourceType: 'file' | 'url',
    source: FileWithPreview | string
  ): Promise<ArrayBuffer> => {
    return sourceType === 'url'
      ? getArrayBufferFromUrl(source as string)
      : getArrayBufferFromFile(source as FileWithPreview);
  };

  const isDuplicateUpload = useCallback(
    async (file: FileWithPreview | ExternalMedia): Promise<boolean> => {
      const isNotDeletedOrFailed = (item: MediaItemUpload) =>
        item.status !== MediaUploadStatus.DELETED && item.status !== MediaUploadStatus.FAILED;

      const isDuplicateOfUrl = (item: MediaItemUpload) =>
        file.sourceType === SourceType.external && item.url === file.url;

      const isDuplicateInNameAndSize = (item: MediaItemUpload) =>
        file.sourceType !== SourceType.external && item.file?.name === file.name && item.file?.size === file.size;

      return Object.values(uploads).some(
        item => isNotDeletedOrFailed(item) && (isDuplicateOfUrl(item) || isDuplicateInNameAndSize(item))
      );
    },
    [uploads]
  );

  const queueUploads = useCallback(
    async (files: FileWithPreview[] | ExternalMedia[], onUploadStatusChange: OnUploadStatusChangeCallbackType) => {
      // This adds a placeholder to the cache
      // This should only happen to photo types... and not here?
      // Videos will be different since we won't have a poster until after processing
      const newUploads: Record<string, MediaItemUpload> = {};

      const processedFiles = await Promise.all(
        files.map(async file => {
          const isExternal = file.sourceType === SourceType.external;
          const id = getUploadIdFromFileOrUrl(file);

          // Duplicate check
          if (await isDuplicateUpload(file)) {
            const upload = uploads[id];
            if (upload) {
              // TODO may need to add a separate duplicate status?
              onUploadStatusChange(upload, MediaUploadStatus.PENDING);
              if (upload.status !== MediaUploadStatus.PENDING) {
                onUploadStatusChange(upload, upload.status);
              }
            }
            return null;
          }

          const source: 'file' | 'url' = isExternal ? 'url' : 'file';
          const url = isExternal ? file.url : URL.createObjectURL(file);
          const type =
            file.type === 'photo'
              ? MediaType.PHOTO
              : file.type.toUpperCase().startsWith('VIDEO')
              ? MediaType.VIDEO
              : MediaType.PHOTO;

          const fileName = url.substring(url.lastIndexOf('/') + 1);
          const length = isExternal ? null : file.length;
          const startTranscode = !isExternal ? file.startTranscode ?? false : false;

          let buffer: ArrayBuffer | null = null;
          try {
            buffer =
              source === 'url' && file.sourceType === SourceType.external
                ? await getMediaUploadItemBuffer(source, file.url)
                : file.sourceType === SourceType.file && typeof file === 'object'
                ? await getMediaUploadItemBuffer(source, file)
                : new ArrayBuffer(10);
          } catch (e) {
            buffer = null;
          }

          const imageDimensions = buffer
            ? type !== MediaType.VIDEO
              ? await getImageDimensions(url)
              : await getVideoDimensions(url)
            : { width: 0, height: 0 };

          const isGifFile = buffer ? isGifAnimated(buffer) : false;

          const uploadData: MediaItemUpload = {
            uploadDoneTimestamp: undefined,
            registrationKey: undefined,
            id,
            timeUploadStarted: Date.now(),
            source,
            fileName,
            type,
            isGif: isGifFile,
            length,
            status: MediaUploadStatus.PENDING,
            onUploadStatusChange,
            completedMediaItem: undefined,
            url,
            height: imageDimensions.height,
            width: imageDimensions.width,
            proportion: getMediaProportion(imageDimensions.width, imageDimensions.height),
            startTranscode,
            canvaId: isExternal && file.canvaId ? file.canvaId : undefined,
            ...(source === 'file' && 'lastModified' in file ? { file } : {})
          };

          return { id, uploadData };
        })
      );

      // add new uploads to the state
      processedFiles
        .filter((result): result is { id: string; uploadData: MediaItemUpload } => result !== null)
        .forEach(({ id, uploadData }) => {
          newUploads[id] = uploadData;
        });

      setUploads(old => ({ ...old, ...newUploads }));
    },
    [isDuplicateUpload, uploads]
  );

  const registerUploads: (uploads: MediaItemUpload[]) => Promise<FetchResult<BulkRegisterMediaUploadsMutation>> =
    useCallback(
      uploads => {
        const input: RegisterMediaInput[] = [];

        uploads.forEach(file => {
          input.push({
            suppliedUploadId: file.id,
            type: file.type,
            extension: file.file?.name.split('.').pop() || ''
          });
        });

        return bulkRegisterUploadsMutation({
          variables: {
            workspaceId: selectedWorkspaceId,
            input
          }
        });
      },
      [bulkRegisterUploadsMutation, selectedWorkspaceId]
    );

  const completeUploads: (uploads: MediaItemUpload[]) => Promise<FetchResult<BulkCompleteMediaUploadsMutation>> =
    useCallback(
      uploads => {
        const input: UploadMediaInput[] = [];
        uploads.forEach(upload => {
          if (!upload.registrationKey) {
            return;
          }
          input.push({
            type: upload.type,
            registrationKey: upload.registrationKey,
            originalFilename: upload.fileName,
            startVideoTranscoding: upload.startTranscode
          });
        });
        return bulkCompleteUploadsMutation({
          variables: {
            workspaceId: selectedWorkspaceId,
            input
          }
        });
      },
      [bulkCompleteUploadsMutation, selectedWorkspaceId]
    );

  const bulkUploadExternalPhotos: (uploads: MediaItemUpload[]) => Promise<FetchResult<BulkAddExternalPhotosMutation>> =
    useCallback(
      uploads => {
        const input: ExternalMediaInput[] = [];
        uploads.forEach(upload => {
          if (!upload.url) {
            return;
          }
          input.push({
            type: MediaType.PHOTO,
            url: upload.url,
            canvaId: upload.canvaId
          });
        });
        return bulkAddExternalPhotosMutation({
          variables: {
            workspaceId: selectedWorkspaceId,
            input
          }
        });
      },
      [bulkAddExternalPhotosMutation, selectedWorkspaceId]
    );

  const changeUploadValue = (id: string, props: Partial<MediaItemUpload>) => {
    setUploads(prev => {
      const upload = prev[id];
      if (!upload) {
        return prev;
      }
      return { ...prev, ...{ [id]: { ...upload, ...props } } };
    });
  };

  const getUploadsByStatus = useCallback(
    (status: MediaUploadStatus): MediaItemUpload[] => {
      return Object.values(uploads).filter(upload => upload.status === status);
    },
    [uploads]
  );

  const processPendingMediaQueue = useCallback(async () => {
    const uploadBatchSize = 30;
    const fileUploadBatch: MediaItemUpload[] = [];
    const externalMediaUploadBatch: MediaItemUpload[] = [];
    const pendingUploads = getUploadsByStatus(MediaUploadStatus.PENDING);
    const currentlyUploading = getUploadsByStatus(MediaUploadStatus.UPLOADING);

    // Always do it in batches, wait for all uploads to finish before starting new batch.
    if (currentlyUploading.length > 0) {
      return;
    }

    let i = 0;
    for (const pendingUpload of pendingUploads) {
      if (i >= uploadBatchSize) {
        break;
      }

      if (pendingUpload.source === 'file' && pendingUpload.file) {
        changeUploadValue(pendingUpload.id, { status: MediaUploadStatus.UPLOADING });
        fileUploadBatch.push(pendingUpload);
      } else if (pendingUpload.source === 'url' && typeof pendingUpload.url !== 'undefined') {
        changeUploadValue(pendingUpload.id, { status: MediaUploadStatus.UPLOADING });
        externalMediaUploadBatch.push(pendingUpload);
      }
      i++;
    }

    if (externalMediaUploadBatch.length > 0) {
      bulkUploadExternalPhotos(externalMediaUploadBatch)
        .then(externalUploadResult => {
          if (!externalUploadResult.data) {
            throw new Error('External media upload failed');
          }
          externalUploadResult.data.bulkAddExternalPhotos.forEach(uploadedItem => {
            const upload = externalMediaUploadBatch.find(upload => upload.url === uploadedItem.suppliedRegistrationKey);
            if (!upload) {
              // Couldn't find the matching upload.
              return;
            }

            if (!uploadedItem.media || uploadedItem.error) {
              changeUploadValue(upload.id, {
                status: MediaUploadStatus.FAILED
              });
            } else {
              changeUploadValue(upload.id, {
                status: MediaUploadStatus.COMPLETED,
                completedMediaItem: uploadedItem.media
              });
            }
          });
        })
        .catch(() => {
          externalMediaUploadBatch.forEach(upload => {
            changeUploadValue(upload.id, { status: MediaUploadStatus.FAILED });
          });
        });
    }

    if (fileUploadBatch.length > 0) {
      registerUploads(fileUploadBatch)
        .then(registerResult => {
          if (!registerResult.data) {
            throw new Error('Registration failed');
          }
          registerResult.data.bulkRegisterMediaItemUploads.forEach(registration => {
            const registrationKey = registration.registrationKey || '';
            const registrationUrl = registration.uploadUrl || '';
            changeUploadValue(registration.suppliedUploadId, { registrationKey: registrationKey });
            const upload = uploads[registration.suppliedUploadId];
            if (!upload?.file) {
              if (upload) {
                changeUploadValue(registration.suppliedUploadId, { status: MediaUploadStatus.FAILED });
              }
              return;
            }
            uploadMediaFileToUrl(registrationKey, registrationUrl, upload.file)
              .then(() => {
                changeUploadValue(registration.suppliedUploadId, {
                  status: MediaUploadStatus.UPLOADING_DONE,
                  uploadDoneTimestamp: Math.round(Date.now() / 1000)
                });
              })
              .catch(() => {
                changeUploadValue(registration.suppliedUploadId, { status: MediaUploadStatus.FAILED });
              });
          });
        })
        .catch(() => {
          fileUploadBatch.forEach(upload => {
            changeUploadValue(upload.id, { status: MediaUploadStatus.FAILED });
          });
        });
    }
  }, [bulkUploadExternalPhotos, getUploadsByStatus, registerUploads, uploads]);

  const processUploadedFileMediaQueue = useCallback(() => {
    const completeBatchSize = 15;
    const maxSecondsToWaitForOldestUpload = 3;
    const nowInSeconds = Math.round(Date.now() / 1000);
    const fileCompletionBatch: MediaItemUpload[] = [];
    const currentlyUploading = getUploadsByStatus(MediaUploadStatus.UPLOADING);
    const waitingToComplete = getUploadsByStatus(MediaUploadStatus.UPLOADING_DONE).sort((a, b) => {
      return a.uploadDoneTimestamp && b.uploadDoneTimestamp
        ? a.uploadDoneTimestamp < b.uploadDoneTimestamp
          ? 1
          : -1
        : 0;
    });
    const oldestUploadDoneTime = waitingToComplete[0]?.uploadDoneTimestamp;
    const oldestUploadTimeLimitReached =
      !!oldestUploadDoneTime && nowInSeconds - oldestUploadDoneTime >= maxSecondsToWaitForOldestUpload;

    // Don't start completing a new batch of uploads if:
    // - We're still uploading, and
    // - The waiting uploads aren't at the batch size, and
    // - The oldest upload has't waited long enough.
    if (
      currentlyUploading.length > 0 &&
      waitingToComplete.length < completeBatchSize &&
      !oldestUploadTimeLimitReached
    ) {
      return;
    }

    let i = 0;
    for (const waitingUpload of waitingToComplete) {
      if (i >= completeBatchSize) {
        break;
      }
      if (waitingUpload.source !== 'file' || !waitingUpload.file) {
        continue;
      }

      changeUploadValue(waitingUpload.id, { status: MediaUploadStatus.COMPLETING });
      fileCompletionBatch.push(waitingUpload);
      i++;
    }

    if (!fileCompletionBatch.length) {
      return;
    }

    completeUploads(fileCompletionBatch)
      .then(result => {
        if (!result.data) {
          throw new Error('Completion failed');
        }
        result.data.bulkCompleteMediaUploads.forEach(completedItem => {
          const upload = fileCompletionBatch.find(
            upload => upload.registrationKey === completedItem.suppliedRegistrationKey
          );
          if (!upload) {
            // Couldn't find the matching upload.
            return;
          }

          if (!completedItem.media || completedItem.error) {
            changeUploadValue(upload.id, {
              status: MediaUploadStatus.FAILED
            });
          } else {
            changeUploadValue(upload.id, {
              status: MediaUploadStatus.COMPLETED,
              completedMediaItem: completedItem.media
            });
          }
        });
      })
      .catch(() => {
        fileCompletionBatch.forEach(upload => {
          changeUploadValue(upload.id, {
            status: MediaUploadStatus.FAILED
          });
        });
      });
  }, [completeUploads, getUploadsByStatus]);

  useEffect(() => {
    // If any upload status is different, process the queue, and call the callback on each upload.
    let statusesChanged = false;

    Object.keys(uploads).forEach(uploadId => {
      const prevUpload = prevState.current[uploadId];
      const currentUpload = uploads[uploadId];
      if (!prevUpload || prevUpload.status !== currentUpload.status) {
        currentUpload.onUploadStatusChange(currentUpload, currentUpload.status);
        statusesChanged = true;
      }
    });

    if (statusesChanged) {
      processPendingMediaQueue();
      processUploadedFileMediaQueue();
    }

    // Make sure we always keep prevState up to date.
    prevState.current = uploads;
  }, [processPendingMediaQueue, processUploadedFileMediaQueue, uploads]);

  // This puts the media file from the user's computer to the another destination (i.e. s3)
  const uploadMediaFileToUrl = (
    registrationKey: string,
    registrationUrl: string,
    file: FileWithPreview
  ): Promise<AxiosResponse> => {
    const options: AxiosRequestConfig<File> = {
      headers: {
        'Content-Type': file.type
      }
    };

    return new Promise((resolve, reject) => {
      axios
        .put(registrationUrl, file, options)
        .then(result => resolve(result))
        .catch(e => reject(e));
    });
  };

  return { uploads, queueUploads, deleteUpload };
};
